import { useState, useEffect, useRef, useCallback } from "react";
import { apiFetch } from "@/lib/api-client";
import { authClient } from "@/lib/auth-client";
import { useAuth } from "@/lib/auth-provider";

/**
 * G.21: Real-time Translation + Captions
 *
 * Two components:
 * 1. CaptionPresenterControls — toggle captions, pick source language, export
 * 2. CaptionAttendeeOverlay — CC button, language picker, caption display
 *
 * Caption data arrives via WebSocket (existing realtime-hub DO).
 * These components manage UI state and call the captions API for
 * session lifecycle and transcript export.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface TranscriptSegment {
  startSeconds: number;
  endSeconds: number;
  text: string;
  speaker?: string;
  language: string;
}

interface CaptionRealtimeMessage {
  text: string;
  language: string;
  isFinal: boolean;
  timestamp: string;
}

interface SupportedLanguage {
  code: string;
  name: string;
  nativeName: string;
}

type FontSize = "sm" | "md" | "lg";

const FONT_SIZE_CLASSES: Record<FontSize, string> = {
  sm: "text-sm",
  md: "text-base",
  lg: "text-xl",
};

// ---------------------------------------------------------------------------
// Presenter Controls
// ---------------------------------------------------------------------------

interface CaptionPresenterControlsProps {
  webinarId: string;
}

export function CaptionPresenterControls({ webinarId }: CaptionPresenterControlsProps) {
  const [manualDraft, setManualDraft] = useState("");
  const [isActive, setIsActive] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [sourceLanguage, setSourceLanguage] = useState("en");
  const [enableTranslation, setEnableTranslation] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [languages, setLanguages] = useState<SupportedLanguage[]>([]);
  const [transcript, setTranscript] = useState<TranscriptSegment[]>([]);
  const [error, setError] = useState<string | null>(null);
  const transcriptEndRef = useRef<HTMLDivElement>(null);

  // Fetch available languages on mount
  useEffect(() => {
    async function fetchLanguages() {
      try {
        const data = await apiFetch<{ languages: SupportedLanguage[] }>(
          `/v1/webinars/${webinarId}/captions/languages`,
        );
        setLanguages(data.languages);
      } catch {
        // Non-critical — defaults will work
      }
    }
    fetchLanguages();
  }, [webinarId]);

  // Auto-scroll transcript preview
  useEffect(() => {
    transcriptEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [transcript]);

  const handleToggleCaptions = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      if (isActive) {
        await apiFetch<{ stopped: boolean; durationSeconds: number }>(
          `/v1/webinars/${webinarId}/captions/stop`,
          { method: "POST" },
        );
        setIsActive(false);
        setSessionId(null);
      } else {
        const data = await apiFetch<{
          sessionId: string;
          status: string;
          sourceLanguage: string;
        }>(`/v1/webinars/${webinarId}/captions/start`, {
          method: "POST",
          body: JSON.stringify({ sourceLanguage, enableTranslation }),
        });
        setIsActive(true);
        setSessionId(data.sessionId);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Caption toggle failed");
    } finally {
      setIsLoading(false);
    }
  }, [isActive, webinarId, sourceLanguage, enableTranslation]);

  const handleExport = useCallback(
    async (format: "vtt" | "srt") => {
      try {
        const data = await apiFetch<{ content: string; filename: string }>(
          `/v1/webinars/${webinarId}/captions/export`,
          {
            method: "POST",
            body: JSON.stringify({ format }),
          },
        );
        // Trigger download
        const blob = new Blob([data.content], { type: "text/plain;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement("a");
        anchor.href = url;
        anchor.download = data.filename;
        anchor.click();
        URL.revokeObjectURL(url);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Export failed");
      }
    },
    [webinarId],
  );

  const handlePublishCaption = useCallback(async () => {
    const text = manualDraft.trim();
    if (!text) {
      return;
    }

    try {
      const data = await apiFetch<{ data: CaptionRealtimeMessage }>(`/v1/webinars/${webinarId}/captions/publish`, {
        method: "POST",
        body: JSON.stringify({
          text,
          language: sourceLanguage,
          isFinal: true,
        }),
      });

      setTranscript((current) => [
        ...current,
        {
          startSeconds: 0,
          endSeconds: 0,
          text: data.data.text,
          language: data.data.language,
        },
      ].slice(-20));
      setManualDraft("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Caption publish failed");
    }
  }, [manualDraft, sourceLanguage, webinarId]);

  // Poll transcript while captions are active (every 5s)
  useEffect(() => {
    if (!isActive) return;

    let cancelled = false;

    async function poll() {
      try {
        const data = await apiFetch<{ segments: TranscriptSegment[] }>(
          `/v1/webinars/${webinarId}/captions/transcript`,
        );
        if (!cancelled) setTranscript(data.segments);
      } catch {
        // Silently retry next interval
      }
    }

    poll();
    const interval = setInterval(poll, 5000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [isActive, webinarId]);

  return (
    <div className="space-y-4 rounded-lg border border-gray-700 bg-gray-800 p-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-white">Live Captions</h3>
        {sessionId && (
          <span className="rounded-full bg-[var(--color-success)]/50 px-2 py-0.5 text-xs text-[var(--color-success)]">
            Active
          </span>
        )}
      </div>

      {error && (
        <p className="text-xs text-[var(--color-destructive)]/80" role="alert">{error}</p>
      )}

      {/* Source language selector */}
      <div className="space-y-1">
        <label htmlFor="source-language" className="text-xs text-[var(--color-muted-foreground)]">
          Source Language
        </label>
        <select
          id="source-language"
          value={sourceLanguage}
          onChange={(e) => setSourceLanguage(e.target.value)}
          disabled={isActive}
          className="w-full rounded-md border border-gray-600 bg-gray-700 px-2 py-1.5 text-sm text-white disabled:opacity-50"
        >
          {languages.map((lang) => (
            <option key={lang.code} value={lang.code}>
              {lang.name} ({lang.nativeName})
            </option>
          ))}
        </select>
      </div>

      {/* Translation toggle */}
      <label className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={enableTranslation}
          onChange={(e) => setEnableTranslation(e.target.checked)}
          disabled={isActive}
          className="h-4 w-4 rounded border-gray-600 bg-gray-700 text-primary focus:ring-primary"
        />
        <span className="text-sm text-gray-300">Enable multi-language translation</span>
      </label>

      {/* Start / Stop button */}
      <button
        onClick={handleToggleCaptions}
        disabled={isLoading}
        className={`w-full rounded-md px-4 py-2 text-sm font-medium transition-colors ${
          isActive
            ? "bg-[var(--color-destructive)] text-white hover:bg-[var(--color-destructive)]"
            : "bg-primary text-white hover:bg-primary/90"
        } disabled:opacity-50`}
      >
        {isLoading
          ? "Processing..."
          : isActive
            ? "Stop Captions"
            : "Start Captions"}
      </button>

      <div className="space-y-2">
        <label htmlFor="manual-caption" className="text-xs text-[var(--color-muted-foreground)]">
          Manual caption line
        </label>
        <textarea
          id="manual-caption"
          value={manualDraft}
          onChange={(event) => setManualDraft(event.target.value)}
          rows={3}
          disabled={!isActive}
          placeholder="Type the live caption line to push to attendees."
          className="w-full rounded-md border border-gray-600 bg-gray-700 px-3 py-2 text-sm text-white disabled:opacity-50"
        />
        <button
          type="button"
          disabled={!isActive || manualDraft.trim().length === 0}
          onClick={() => void handlePublishCaption()}
          className="w-full rounded-md border border-gray-600 px-3 py-2 text-sm text-gray-200 hover:bg-gray-700 disabled:opacity-50"
        >
          Publish caption line
        </button>
      </div>

      {/* Live transcript preview */}
      {isActive && (
        <div className="max-h-48 overflow-y-auto rounded-md bg-gray-900 p-2">
          <p className="mb-1 text-xs text-[var(--color-muted-foreground)]">Live Transcript</p>
          {transcript.length === 0 ? (
            <p className="text-xs italic text-[var(--color-muted-foreground)]">Waiting for speech...</p>
          ) : (
            <div className="space-y-1">
              {transcript.slice(-20).map((seg, i) => (
                <p key={i} className="text-xs text-gray-300">
                  {seg.speaker && (
                    <span className="font-medium text-[var(--color-muted-foreground)]">{seg.speaker}: </span>
                  )}
                  {seg.text}
                </p>
              ))}
              <div ref={transcriptEndRef} />
            </div>
          )}
        </div>
      )}

      {/* Export buttons */}
      <div className="flex gap-2">
        <button
          onClick={() => handleExport("vtt")}
          className="flex-1 rounded-md border border-gray-600 px-3 py-1.5 text-xs text-gray-300 hover:bg-gray-700"
        >
          Export WebVTT
        </button>
        <button
          onClick={() => handleExport("srt")}
          className="flex-1 rounded-md border border-gray-600 px-3 py-1.5 text-xs text-gray-300 hover:bg-gray-700"
        >
          Export SRT
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Attendee Caption Overlay
// ---------------------------------------------------------------------------

interface CaptionAttendeeOverlayProps {
  webinarId: string;
}

export function CaptionAttendeeOverlay({ webinarId }: CaptionAttendeeOverlayProps) {
  useAuth();
  const [isVisible, setIsVisible] = useState(false);
  const [fontSize, setFontSize] = useState<FontSize>("md");
  const [isHighContrast, setIsHighContrast] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState("en");
  const [languages, setLanguages] = useState<SupportedLanguage[]>([]);
  const [currentCaption, setCurrentCaption] = useState<string>("");
  const [showSettings, setShowSettings] = useState(false);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wsRef = useRef<WebSocket | null>(null);

  const storage = getBrowserStorage();

  useEffect(() => {
    const storedVisible = storage?.getItem(`webinar-caption-visible:${webinarId}`);
    const storedLanguage = storage?.getItem(`webinar-caption-language:${webinarId}`);
    const storedFontSize = storage?.getItem(`webinar-caption-font:${webinarId}`);
    const storedContrast = storage?.getItem(`webinar-caption-contrast:${webinarId}`);

    if (storedVisible === "true") setIsVisible(true);
    if (storedLanguage) setSelectedLanguage(storedLanguage);
    if (storedFontSize === "sm" || storedFontSize === "md" || storedFontSize === "lg") setFontSize(storedFontSize);
    if (storedContrast === "true") setIsHighContrast(true);
  }, [storage, webinarId]);

  useEffect(() => {
    storage?.setItem(`webinar-caption-visible:${webinarId}`, String(isVisible));
  }, [isVisible, storage, webinarId]);

  useEffect(() => {
    storage?.setItem(`webinar-caption-language:${webinarId}`, selectedLanguage);
  }, [selectedLanguage, storage, webinarId]);

  useEffect(() => {
    storage?.setItem(`webinar-caption-font:${webinarId}`, fontSize);
  }, [fontSize, storage, webinarId]);

  useEffect(() => {
    storage?.setItem(`webinar-caption-contrast:${webinarId}`, String(isHighContrast));
  }, [isHighContrast, storage, webinarId]);

  // Fetch available languages
  useEffect(() => {
    async function fetchLanguages() {
      try {
        const data = await apiFetch<{ languages: SupportedLanguage[] }>(
          `/v1/webinars/${webinarId}/captions/languages`,
        );
        setLanguages(data.languages);
      } catch {
        // Defaults remain
      }
    }
    fetchLanguages();
  }, [webinarId]);

  // Set preferred translation language
  const handleLanguageChange = useCallback(
    async (code: string) => {
      setSelectedLanguage(code);
      try {
        await apiFetch(`/v1/webinars/${webinarId}/captions/translate`, {
          method: "POST",
          body: JSON.stringify({ languageCode: code }),
        });
      } catch {
        // Language preference is also stored locally; API call is best-effort
      }
    },
    [webinarId],
  );

  useEffect(() => {
    if (!isVisible || !webinarId) return;

    let cancelled = false;

    async function connect() {
      const tokenMethod = (authClient as unknown as { token?: () => Promise<unknown> }).token;
      if (typeof tokenMethod !== "function") {
        return;
      }

      const token = extractBearerToken(await tokenMethod.call(authClient));
      if (!token || cancelled) {
        return;
      }

      const ws = new WebSocket(buildCaptionRealtimeUrl(resolveRealtimeBaseUrl(), token, webinarId));
      wsRef.current = ws;
      ws.addEventListener("message", (event) => {
        const payload = parseCaptionEnvelope(event.data);
        if (!payload || payload.language !== selectedLanguage) {
          return;
        }

        setCurrentCaption(payload.text);
        if (hideTimerRef.current) {
          clearTimeout(hideTimerRef.current);
        }
        hideTimerRef.current = setTimeout(() => {
          setCurrentCaption("");
        }, 5_000);
      });
    }

    void connect();

    return () => {
      cancelled = true;
      wsRef.current?.close();
      wsRef.current = null;
    };
  }, [isVisible, selectedLanguage, webinarId]);

  const bgClass = isHighContrast
    ? "bg-black/95"
    : "bg-black/70";

  const textClass = isHighContrast
    ? "text-white font-bold"
    : "text-white";

  return (
    <>
      <div className="absolute right-4 bottom-4 z-30 flex items-center gap-2">
        <button
          onClick={() => setIsVisible((prev) => !prev)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setIsVisible((prev) => !prev);
            }
          }}
          aria-label={isVisible ? "Hide closed captions" : "Show closed captions"}
          aria-pressed={isVisible}
          className={`relative rounded-md px-3 py-2 text-sm font-medium transition-colors ${
            isVisible
              ? "bg-background text-black"
              : "bg-gray-700 text-white hover:bg-gray-600"
          }`}
        >
          CC
        </button>

        {isVisible && (
          <button
            onClick={() => setShowSettings((prev) => !prev)}
            aria-label="Caption settings"
            className="rounded-md bg-gray-700 px-2 py-2 text-sm text-white hover:bg-gray-600"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4"
              viewBox="0 0 20 20"
              fill="currentColor"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        )}
      </div>

      {/* Settings panel */}
      {showSettings && (
        <div className="absolute right-4 bottom-16 z-50 w-64 rounded-lg border border-gray-700 bg-gray-800 p-4 shadow-xl">
          <h4 className="mb-3 text-sm font-semibold text-white">Caption Settings</h4>

          {/* Language selector */}
          <div className="mb-3 space-y-1">
            <label htmlFor="caption-language" className="text-xs text-[var(--color-muted-foreground)]">
              Language
            </label>
            <select
              id="caption-language"
              value={selectedLanguage}
              onChange={(e) => handleLanguageChange(e.target.value)}
              className="w-full rounded-md border border-gray-600 bg-gray-700 px-2 py-1.5 text-sm text-white"
            >
              {languages.map((lang) => (
                <option key={lang.code} value={lang.code}>
                  {lang.nativeName} ({lang.name})
                </option>
              ))}
            </select>
          </div>

          {/* Font size */}
          <div className="mb-3 space-y-1">
            <span className="text-xs text-[var(--color-muted-foreground)]">Font Size</span>
            <div className="flex gap-1">
              {(["sm", "md", "lg"] as const).map((size) => (
                <button
                  key={size}
                  onClick={() => setFontSize(size)}
                  aria-label={`Font size ${size === "sm" ? "small" : size === "md" ? "medium" : "large"}`}
                  className={`flex-1 rounded-md px-2 py-1 text-xs transition-colors ${
                    fontSize === size
                      ? "bg-primary text-white"
                      : "bg-gray-700 text-[var(--color-muted-foreground)] hover:bg-gray-600"
                  }`}
                >
                  {size === "sm" ? "S" : size === "md" ? "M" : "L"}
                </button>
              ))}
            </div>
          </div>

          {/* High contrast toggle */}
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={isHighContrast}
              onChange={(e) => setIsHighContrast(e.target.checked)}
              className="h-4 w-4 rounded border-gray-600 bg-gray-700 text-primary focus:ring-primary"
            />
            <span className="text-xs text-gray-300">High contrast mode</span>
          </label>
        </div>
      )}

      {/* Caption overlay */}
      {isVisible && currentCaption && (
        <div
          role="region"
          aria-label="Live captions"
          aria-live="polite"
          aria-atomic="true"
          className={`pointer-events-none absolute inset-x-0 bottom-24 mx-auto max-w-2xl px-4 transition-opacity duration-300`}
        >
          <div className={`rounded-md ${bgClass} px-4 py-2 text-center`}>
            <p className={`${FONT_SIZE_CLASSES[fontSize]} ${textClass} leading-relaxed`}>
              {currentCaption}
            </p>
          </div>
        </div>
      )}

      {/* Screen reader only: announce new captions */}
      <div className="sr-only" aria-live="assertive" aria-atomic="true">
        {isVisible && currentCaption ? `Caption: ${currentCaption}` : ""}
      </div>
    </>
  );
}

function resolveRealtimeBaseUrl(): string {
  const env = import.meta.env as Record<string, string | undefined>;
  return env.VITE_REALTIME_URL?.trim() || window.location.origin;
}

function buildCaptionRealtimeUrl(baseUrl: string, token: string, webinarId: string) {
  const url = new URL(baseUrl);
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  url.pathname = "/ws";
  url.searchParams.set("token", token);
  url.searchParams.set("channels", `webinar.${webinarId}.captions`);
  return url.toString();
}

function extractBearerToken(result: unknown): string | null {
  const candidates = [
    pickValue(result, ["data", "token"]),
    pickValue(result, ["token"]),
    pickValue(result, ["data", "accessToken"]),
    pickValue(result, ["accessToken"]),
  ];

  return candidates.find((value): value is string => typeof value === "string" && value.trim().length > 0) ?? null;
}

function pickValue(input: unknown, path: string[]): unknown {
  let current: unknown = input;
  for (const segment of path) {
    if (!current || typeof current !== "object" || !(segment in current)) {
      return null;
    }
    current = (current as Record<string, unknown>)[segment];
  }
  return current;
}

function getBrowserStorage(): Storage | null {
  if (typeof window === "undefined") {
    return null;
  }

  const candidate = window.localStorage;
  if (
    candidate
    && typeof candidate.getItem === "function"
    && typeof candidate.setItem === "function"
  ) {
    return candidate;
  }

  return null;
}

function parseCaptionEnvelope(raw: unknown): CaptionRealtimeMessage | null {
  try {
    const parsed = JSON.parse(typeof raw === "string" ? raw : String(raw)) as {
      type?: string;
      payload?: { caption?: CaptionRealtimeMessage };
    };

    if (parsed.type !== "webinar.caption.text" || !parsed.payload?.caption) {
      return null;
    }

    return parsed.payload.caption;
  } catch {
    return null;
  }
}
